import React, { useEffect, useState } from "react";

import consultAnExpert from "../../api/consultancy";
import FormStatus, { StatusType } from "./form-status";
import { getHeardFromMoreDetails, PAGE_PATHS } from "../../constants";
import { InlineWidget, useCalendlyEventListener } from "react-calendly";
import Modal from "../../useOss/Modal";
// import { dispatchTwqEvent } from "../../utils/twq";

const notAllowedDomains = ["gmail", "hotmail", "yahoo", "outlook"];

import { sendEventToGoogleAnalytics, sendGenericAnalyticsEvent, sendGenericAnalyticsEventAsync } from "../../utils";
import { UTM_SOURCE_STORAGE_KEY } from "../../utils/ads";
import "../../style/consultancy-form.module.css";

import { heardFromOptions } from "../../constants";
import sendHeardAboutUsData from "../../api/heardAboutUs";
import { isValidEmail } from "../../dashboard/saas/utils";

export default function ConsultancyForm({
    visibilityClass,
    containerClass
}: {
    visibilityClass: string;
    containerClass: string;
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState<StatusType>("idle");
    const [heardFrom, setHeardFrom] = useState("");
    const [heardFromDetails, setHeardFromDetails] = useState("");
    const [emailError, setEmailError] = useState<string | null>(null);
    const [nameError, setNameError] = useState<string | null>(null);

    const isSubmitDisabled = isLoading || !!emailError || !!nameError;

    const howDidYouHearAboutUsDetails = getHeardFromMoreDetails(heardFrom);

    useEffect(() => {
        const originalStyle = window.getComputedStyle(document.body).overflow;

        if (status === "success") {
            document.body.style.overflow = "hidden";
            document.body.style.height = "100%";
        } else {
            document.body.style.overflow = originalStyle;
            document.body.style.height = "auto";
        }

        return () => {
            document.body.style.overflow = originalStyle;
            document.body.style.height = "auto";
        };
    }, [status]);

    function workingEmailValidator(email: string) {
        const isEmail = isValidEmail(email);
        if (!isEmail) {
            return false;
        }
        const isNotAWorkingMail = notAllowedDomains.includes(email.split("@")[1].split(".")[0]);
        if (isNotAWorkingMail) {
            return false;
        }
        return true;
    }

    async function handleFormSubmission(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        setIsLoading(true);
        const name = e.currentTarget.fullName.value;
        const email = e.currentTarget.email.value;
        const question = e.currentTarget.question.value;

        try {
            await consultAnExpert({ email, name, field: question });
            setStatus("success");
            sendGenericAnalyticsEvent("sales_demo_form_submitted");
            sendEventToGoogleAnalytics("sales_demo_form_submitted", {
                email,
                utm_source: localStorage.getItem(UTM_SOURCE_STORAGE_KEY) || ""
            });
            sendEventToGoogleAnalytics("conversion", {
                send_to: "AW-11492917308/o-1xCNHNjPsZELyAoOgq"
            });
        } catch (error) {
            setStatus("error");
            sendGenericAnalyticsEvent("sales_demo_form_error");
            setTimeout(() => {
                setStatus("idle");
            }, 5000);
        } finally {
            setIsLoading(false);
            // We don't want this to stall the form submission
            try {
                if (heardFrom) {
                    sendHeardAboutUsData({ source: heardFrom, detail: heardFromDetails, name, email });
                }
            } catch (error) {
                // Do nothing
            }
        }
    }

    return (
        <section className={`${containerClass} form-status-${status}`}>
            <div className={"consultancy__form__container " + visibilityClass}>
                {status === "success" ? (
                    <Modal
                        show
                        modalClosed={() => setStatus("idle")}
                        className={`calendly__schedule__form__modal ${status === "success" ? "hide" : ""}`}
                    >
                        <div className="calendly__schedule__form__container">
                            <div className="calendly__schedule__form__header">
                                <FormStatus status="success" />

                                <svg
                                    width="29"
                                    height="29"
                                    viewBox="0 0 29 29"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    cursor="pointer"
                                    onClick={() => setStatus("idle")}
                                >
                                    <circle cx="14.5" cy="14.5" r="14.5" fill="white" fillOpacity="0.14" />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M9.046 8.45988C8.7752 8.52012 8.56017 8.71865 8.47342 8.9885C8.41155 9.18103 8.42529 9.36354 8.51607 9.55483L8.5841 9.69817L10.985 12.1019L13.3859 14.5057L10.9832 16.9096C8.62569 19.2682 8.57915 19.3161 8.51358 19.4511C8.45015 19.5817 8.44671 19.5979 8.44634 19.7679C8.44587 19.9776 8.48576 20.1075 8.59492 20.252C8.84506 20.583 9.31445 20.6634 9.65734 20.434C9.71106 20.398 10.8014 19.3211 12.1268 17.9948L14.5001 15.6201L16.8915 18.0101C18.2067 19.3246 19.319 20.4225 19.3633 20.4498C19.5479 20.5638 19.8193 20.5975 20.0135 20.5307C20.2378 20.4535 20.3898 20.3184 20.4951 20.1028C20.5502 19.9899 20.5536 19.9715 20.5541 19.7858C20.5546 19.5932 20.553 19.5855 20.4859 19.4481C20.4181 19.3094 20.3849 19.2751 18.0167 16.9056L15.6161 14.5038L17.9972 12.1189C19.3068 10.8072 20.3982 9.70578 20.4227 9.67129C20.5596 9.47803 20.5999 9.21493 20.5271 8.9885C20.406 8.6118 20.037 8.38651 19.6467 8.45099C19.3856 8.49412 19.5283 8.36216 16.9077 10.9832C15.5895 12.3016 14.5069 13.3802 14.5019 13.3802C14.497 13.3802 13.4144 12.3018 12.0963 10.9838C9.50316 8.39081 9.61885 8.49847 9.37856 8.45445C9.24749 8.43044 9.17294 8.43166 9.046 8.45988Z"
                                        fill="#A1A1A1"
                                    />
                                </svg>
                            </div>
                            <ScheduleCalendlyEvent />
                        </div>
                    </Modal>
                ) : (
                    <div className={"consultancy__form box__gradient"}>
                        <div>
                            <form onSubmit={handleFormSubmission} onChange={() => setStatus("idle")}>
                                <label htmlFor="fullName" className="required">
                                    Full Name
                                </label>
                                <input
                                    required
                                    type="text"
                                    name="fullName"
                                    id="fullName"
                                    placeholder="Jane Doe"
                                    className={nameError ? "form_input_error" : ""}
                                    onBlur={e => {
                                        if (!e.target.value) {
                                            setNameError("Please enter your full name.");
                                        }
                                    }}
                                    onChange={e => {
                                        if (e.target.value) {
                                            setNameError(null);
                                        }
                                    }}
                                />
                                {!!nameError ? <div className="form_input_error_message">{nameError}</div> : null}

                                <label htmlFor="email" className="required">
                                    Work Email Address
                                </label>
                                <input
                                    required
                                    minLength={3}
                                    type="email"
                                    name="email"
                                    id="email"
                                    placeholder="jane123@example.com"
                                    className={emailError ? "form_input_error" : ""}
                                    onChange={e => {
                                        setEmailError(null);
                                    }}
                                    onBlur={e => {
                                        const isWorkingEmail = workingEmailValidator(e.target.value);
                                        if (!isWorkingEmail) {
                                            setEmailError("Please enter your Business / Work email address.");
                                        }
                                    }}
                                />
                                {!!emailError ? (
                                    <div className="form_input_error_message">
                                        Please enter your Business / Work email address.
                                    </div>
                                ) : null}

                                <label htmlFor="question">Message</label>
                                <input type="text" name="question" id="question" placeholder="Type here" />

                                <p>
                                    Help us solve the mystery! 🕵️‍♂️ Let us know how you found us so we can thank the
                                    internet gods (or your awesome friend).
                                </p>

                                <label htmlFor="heardFrom">How did you hear about us?</label>
                                <select
                                    name="heardFrom"
                                    id="heardFrom"
                                    value={heardFrom || ""}
                                    onChange={e => setHeardFrom(e.target.value)}
                                >
                                    <option value="" disabled>
                                        Select an option
                                    </option>
                                    {heardFromOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.text}
                                        </option>
                                    ))}
                                </select>

                                {(() => {
                                    if (!howDidYouHearAboutUsDetails) return null;
                                    switch (howDidYouHearAboutUsDetails.type) {
                                        case "textInput":
                                            return (
                                                <>
                                                    <label htmlFor="details">
                                                        {howDidYouHearAboutUsDetails.description}
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="details"
                                                        id="details"
                                                        placeholder=""
                                                        onChange={e => setHeardFromDetails(e.target.value)}
                                                        autoFocus
                                                    />
                                                </>
                                            );
                                        case "dropdown":
                                            return (
                                                <>
                                                    <label htmlFor="details">
                                                        {howDidYouHearAboutUsDetails.description}
                                                    </label>{" "}
                                                    <select
                                                        name="details"
                                                        id="details"
                                                        placeholder="Select an option"
                                                        onChange={e => setHeardFromDetails(e.target.value)}
                                                        value={heardFromDetails}
                                                    >
                                                        <option value="" disabled>
                                                            Select an option
                                                        </option>
                                                        {howDidYouHearAboutUsDetails.options.map(option => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.text}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </>
                                            );
                                        default:
                                            return null;
                                    }
                                })()}

                                <button disabled={isSubmitDisabled} className="consultancy__submit__btn" type="submit">
                                    Schedule a call
                                </button>
                                {status === "error" ? (
                                    <div className="form_input_error_message">
                                        Oops! something went wrong while submitting the form. Please try again.
                                    </div>
                                ) : null}
                                <a href="/discord" target="_blank" className="join__discord">
                                    Or Join our <b>Discord</b>{" "}
                                    <img src="/static/assets/consultancy/discord.svg" alt="discord" />
                                </a>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

export function ScheduleCalendlyEvent() {
    const [state, setState] = useState<"IDLE" | "TIME_SELECTED">("IDLE");
    useCalendlyEventListener({
        onEventScheduled: () => {
            // dispatchTwqEvent('event', 'tw-oln73-olykx', {});
            sendGenericAnalyticsEventAsync("sales_calendly_event_scheduled")
                .then(() => {
                    window.location.assign(PAGE_PATHS.eventScheduled);
                })
                .catch(() => {});
        },
        onDateAndTimeSelected: () => {
            setState("TIME_SELECTED");
        }
    });

    return (
        <div
            className="calendly__form"
            id="schedule-call"
            style={{
                height: `${state === "IDLE" ? "660px" : "1000px"}`,
                maxHeight: "calc(100vh - 290px)",
                overflow: "scroll"
            }}
        >
            <InlineWidget
                url={`https://calendly.com/d/2fv-zb9-6jk/30-mins-user-call`}
                styles={{
                    height: "100%"
                }}
            />
        </div>
    );
}
